import React from 'react';
import i18n from '../lib/i18n';

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console in full — visible in browser devtools even in production
    console.error('[ErrorBoundary] Uncaught error:', error);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-2xl w-full space-y-4">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 space-y-3">
            <h1 className="text-lg font-semibold text-destructive">{i18n.t('error.boundary.title')}</h1>
            <p className="text-sm font-mono text-destructive/80 break-all">{error.message}</p>
            {error.stack && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  {i18n.t('error.boundary.stackTrace')}
                </summary>
                <pre className="mt-2 overflow-auto rounded bg-muted p-3 text-muted-foreground whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => this.setState({ error: null })}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90"
            >
              {i18n.t('error.boundary.tryAgain')}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-md border text-sm hover:bg-muted"
            >
              {i18n.t('error.boundary.reloadPage')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
